import { D2DBuyerOrderItem } from "../../../../api/d2d/d2dApiInterfaces";

export interface GroupedD2DOrder {
  id: string;
  orderNumber: string;
  dealerReference: string;
  partNumber: string;
  requestedQuantity: number;
  acceptedQuantity: number;
  sellerAccountId: string;
  dateRequested: string;
  carrierName?: string | null;
  trackingNumber?: string | null;
  status: string;
  lastChangedBy?: string;
  rejectionType?: string | null;
  rejectionTrigger?: string | null;
  isPartiallyAccepted?: boolean;
}

export const groupD2DOrders = (
  orders: D2DBuyerOrderItem[]
): GroupedD2DOrder[] => {
  const orderGroups = orders.reduce((acc, order) => {
    const key = `${order.orderNumber}\u0000${order.partNumber ?? ""}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(order);
    return acc;
  }, {} as Record<string, D2DBuyerOrderItem[]>);

  const groupedOrders: GroupedD2DOrder[] = [];

  Object.values(orderGroups).forEach((orderLines) => {
    const { orderNumber } = orderLines[0];
    const idPrefix = `${orderNumber}-${orderLines[0].partNumber ?? ""}`;

    const fullyAcceptedLines = orderLines.filter(
      (line) =>
        line.status === "ACCEPTED" &&
        line.requestedQuantity === line.acceptedQuantity
    );

    if (fullyAcceptedLines.length > 0) {
      const acceptedLinesBySellerAccount = fullyAcceptedLines.reduce(
        (acc, line) => {
          const sellerId = line.sellerAccountId || "";
          if (!acc[sellerId]) {
            acc[sellerId] = [];
          }
          acc[sellerId].push(line);
          return acc;
        },
        {} as Record<string, D2DBuyerOrderItem[]>
      );

      Object.entries(acceptedLinesBySellerAccount).forEach(
        ([sellerId, lines]) => {
          const firstLine = lines[0];
          groupedOrders.push({
            id: `${idPrefix}-accepted-${sellerId}`,
            orderNumber,
            dealerReference: firstLine.dealerReference,
            partNumber: firstLine.partNumber,
            requestedQuantity: firstLine.requestedQuantity,
            acceptedQuantity: firstLine.acceptedQuantity,
            sellerAccountId: sellerId,
            dateRequested: firstLine.dateRequested,
            carrierName: firstLine.carrierName,
            trackingNumber: firstLine.trackingNumber,
            status: "ACCEPTED",
          });
        }
      );
      return;
    }

    const fullyShippedLines = orderLines.filter(
      (line) =>
        ["SHIPPED", "COMPLETED", "CREDITED"].includes(line.status) &&
        line.requestedQuantity === line.acceptedQuantity
    );

    if (fullyShippedLines.length > 0) {
      const shippedLinesBySellerAccount = fullyShippedLines.reduce(
        (acc, line) => {
          const sellerId = line.sellerAccountId || "";
          if (!acc[sellerId]) {
            acc[sellerId] = [];
          }
          acc[sellerId].push(line);
          return acc;
        },
        {} as Record<string, D2DBuyerOrderItem[]>
      );

      Object.entries(shippedLinesBySellerAccount).forEach(
        ([sellerId, lines]) => {
          const firstLine = lines[0];
          groupedOrders.push({
            id: `${idPrefix}-${firstLine.status.toLowerCase()}-${sellerId}`,
            orderNumber,
            dealerReference: firstLine.dealerReference,
            partNumber: firstLine.partNumber,
            requestedQuantity: firstLine.requestedQuantity,
            acceptedQuantity: firstLine.acceptedQuantity,
            sellerAccountId: sellerId,
            dateRequested: firstLine.dateRequested,
            carrierName: firstLine.carrierName,
            trackingNumber: firstLine.trackingNumber,
            status: firstLine.status,
          });
        }
      );
      return;
    }

    const allNew = orderLines.every((line) => line.status === "NEW");
    if (allNew) {
      const firstLine = orderLines[0];
      groupedOrders.push({
        id: `${idPrefix}-new`,
        orderNumber,
        dealerReference: firstLine.dealerReference,
        partNumber: firstLine.partNumber,
        requestedQuantity: firstLine.requestedQuantity,
        acceptedQuantity: firstLine.acceptedQuantity,
        sellerAccountId: "",
        dateRequested: firstLine.dateRequested,
        carrierName: firstLine.carrierName,
        trackingNumber: firstLine.trackingNumber,
        status: "NEW",
      });
      return;
    }

    const allRejected = orderLines.every((line) => line.status === "REJECTED");
    if (allRejected) {
      const firstLine = orderLines[0];
      groupedOrders.push({
        id: `${idPrefix}-rejected`,
        orderNumber,
        dealerReference: firstLine.dealerReference,
        partNumber: firstLine.partNumber,
        requestedQuantity: firstLine.requestedQuantity,
        acceptedQuantity: firstLine.acceptedQuantity,
        sellerAccountId: "",
        dateRequested: firstLine.dateRequested,
        carrierName: firstLine.carrierName,
        trackingNumber: firstLine.trackingNumber,
        status: "REJECTED",
        lastChangedBy: firstLine.lastChangedBy,
        rejectionType: firstLine.rejectionType,
        rejectionTrigger: firstLine.rejectionTrigger,
      });
      return;
    }

    const allExpired = orderLines.every((line) => line.status === "EXPIRED");
    if (allExpired) {
      const firstLine = orderLines[0];
      groupedOrders.push({
        id: `${idPrefix}-expired`,
        orderNumber,
        dealerReference: firstLine.dealerReference,
        partNumber: firstLine.partNumber,
        requestedQuantity: firstLine.requestedQuantity,
        acceptedQuantity: firstLine.acceptedQuantity,
        sellerAccountId: "",
        dateRequested: firstLine.dateRequested,
        carrierName: firstLine.carrierName,
        trackingNumber: firstLine.trackingNumber,
        status: "EXPIRED",
      });
      return;
    }

    const hasAcceptedLines = orderLines.some(
      (line) => line.status === "ACCEPTED"
    );

    if (hasAcceptedLines) {
      const acceptedLinesBySellerAccount = orderLines
        .filter((line) => line.status === "ACCEPTED")
        .reduce((acc, line) => {
          const sellerId = line.sellerAccountId || "";
          if (!acc[sellerId]) {
            acc[sellerId] = [];
          }
          acc[sellerId].push(line);
          return acc;
        }, {} as Record<string, D2DBuyerOrderItem[]>);

      Object.entries(acceptedLinesBySellerAccount).forEach(
        ([sellerId, lines]) => {
          const firstLine = lines[0];
          groupedOrders.push({
            id: `${idPrefix}-accepted-${sellerId}`,
            orderNumber,
            dealerReference: firstLine.dealerReference,
            partNumber: firstLine.partNumber,
            requestedQuantity: firstLine.requestedQuantity,
            acceptedQuantity: firstLine.acceptedQuantity,
            sellerAccountId: sellerId,
            dateRequested: firstLine.dateRequested,
            carrierName: firstLine.carrierName,
            trackingNumber: firstLine.trackingNumber,
            status: "ACCEPTED",
          });
        }
      );

      const nonAcceptedLines = orderLines.filter(
        (line) => line.status !== "ACCEPTED" && line.status !== "REJECTED"
      );

      if (nonAcceptedLines.length > 0) {
        const firstNonAcceptedLine = nonAcceptedLines[0];
        groupedOrders.push({
          id: `${idPrefix}-pending`,
          orderNumber,
          dealerReference: firstNonAcceptedLine.dealerReference,
          partNumber: firstNonAcceptedLine.partNumber,
          requestedQuantity: firstNonAcceptedLine.requestedQuantity,
          acceptedQuantity: firstNonAcceptedLine.acceptedQuantity,
          sellerAccountId: "",
          dateRequested: firstNonAcceptedLine.dateRequested,
          carrierName: firstNonAcceptedLine.carrierName,
          trackingNumber: firstNonAcceptedLine.trackingNumber,
          status: firstNonAcceptedLine.status,
        });
      }
      return;
    }

    const allShippedOrLater = orderLines.every((line) =>
      ["SHIPPED", "COMPLETED", "CREDITED"].includes(line.status)
    );
    if (allShippedOrLater) {
      const firstLine = orderLines[0];
      groupedOrders.push({
        id: `${idPrefix}-${firstLine.status.toLowerCase()}`,
        orderNumber,
        dealerReference: firstLine.dealerReference,
        partNumber: firstLine.partNumber,
        requestedQuantity: firstLine.requestedQuantity,
        acceptedQuantity: firstLine.acceptedQuantity,
        sellerAccountId: firstLine.sellerAccountId,
        dateRequested: firstLine.dateRequested,
        carrierName: firstLine.carrierName,
        trackingNumber: firstLine.trackingNumber,
        status: firstLine.status,
      });
      return;
    }

    const hasShippedLines = orderLines.some((line) =>
      ["SHIPPED", "COMPLETED", "CREDITED"].includes(line.status)
    );
    const hasNewLines = orderLines.some((line) => line.status === "NEW");

    if (hasShippedLines && hasNewLines) {
      const shippedLines = orderLines.filter((line) =>
        ["SHIPPED", "COMPLETED", "CREDITED"].includes(line.status)
      );
      shippedLines.forEach((line, index) => {
        groupedOrders.push({
          id: `${idPrefix}-${line.status.toLowerCase()}-${index}`,
          orderNumber,
          dealerReference: line.dealerReference,
          partNumber: line.partNumber,
          requestedQuantity: line.requestedQuantity,
          acceptedQuantity: line.acceptedQuantity,
          sellerAccountId: line.sellerAccountId,
          dateRequested: line.dateRequested,
          carrierName: line.carrierName,
          trackingNumber: line.trackingNumber,
          status: line.status,
        });
      });

      const firstNewLine = orderLines.find((line) => line.status === "NEW");
      if (firstNewLine) {
        groupedOrders.push({
          id: `${idPrefix}-new`,
          orderNumber,
          dealerReference: firstNewLine.dealerReference,
          partNumber: firstNewLine.partNumber,
          requestedQuantity: firstNewLine.requestedQuantity,
          acceptedQuantity: firstNewLine.acceptedQuantity,
          sellerAccountId: "",
          dateRequested: firstNewLine.dateRequested,
          carrierName: firstNewLine.carrierName,
          trackingNumber: firstNewLine.trackingNumber,
          status: "NEW",
        });
      }
      return;
    }

    const hasRejectedLines = orderLines.some(
      (line) => line.status === "REJECTED"
    );
    const hasRequestedLines = orderLines.some((line) => line.status === "NEW");

    if (hasRejectedLines && hasRequestedLines) {
      const firstNewLine = orderLines.find((line) => line.status === "NEW");
      if (firstNewLine) {
        groupedOrders.push({
          id: `${idPrefix}-new`,
          orderNumber,
          dealerReference: firstNewLine.dealerReference,
          partNumber: firstNewLine.partNumber,
          requestedQuantity: firstNewLine.requestedQuantity,
          acceptedQuantity: firstNewLine.acceptedQuantity,
          sellerAccountId: "",
          dateRequested: firstNewLine.dateRequested,
          carrierName: firstNewLine.carrierName,
          trackingNumber: firstNewLine.trackingNumber,
          status: "NEW",
        });
      }
      return;
    }

    // NEW/EXPIRED rows have no seller, so one row per status is enough; otherwise a request
    // fanned out to many sellers would render identical duplicate rows.
    const seenSellerlessStatuses = new Set<string>();
    orderLines.forEach((line, index) => {
      if (line.status === "REJECTED") {
        return;
      }
      let sellerAccountId = line.sellerAccountId || "";
      if (line.status === "NEW" || line.status === "EXPIRED") {
        if (seenSellerlessStatuses.has(line.status)) {
          return;
        }
        seenSellerlessStatuses.add(line.status);
        sellerAccountId = "";
      }
      groupedOrders.push({
        id: `${idPrefix}-${index}`,
        orderNumber: line.orderNumber,
        dealerReference: line.dealerReference,
        partNumber: line.partNumber,
        requestedQuantity: line.requestedQuantity,
        acceptedQuantity: line.acceptedQuantity,
        sellerAccountId,
        dateRequested: line.dateRequested,
        carrierName: line.carrierName,
        trackingNumber: line.trackingNumber,
        status: line.status,
      });
    });
  });

  return groupedOrders;
};
